import React, { useContext, useEffect, useMemo, useState } from 'react'
import objectPath from 'object-path'
import {
  Modal,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap'
import { useRouteMatch, useLocation } from 'react-router-dom'
import { DesignContext } from 'contexts/DesignContext'
import { createTheme, MuiThemeProvider } from '@material-ui/core'
import { themeDefault } from 'designer/component/display/themes/layoutTheme'
import FeatherIcon from 'feather-icons-react'
import httpClient from 'utility/httpClient.util'
import ModalCreate from './ModalCreate'
import Swal from 'sweetalert2'
import Table from 'designer/component/element/table/Table'
import { createColumn } from 'designer/component/action/Table'
import { createRenderTableData, createSelectFavorite } from './SelectFavorite'
import useAuth from 'hooks/useAuth'
import styles from 'designer/styles/modal.module.css'
import cloneDeep from 'clone-deep'

const ModalSelect = ({
  menu,
  isOpen = false,
  setIsOpen,
  uischema,
  isChart,
  chartData,
  config,
  dynamicData = {},
}) => {
  const designContext = useContext(DesignContext)
  const match = useRouteMatch()
  const location = useLocation()
  let receivePathParams = match.params
  let urlSearchParams = new URLSearchParams(location.search || [])
  let receiveQueryParams = Object.fromEntries([...urlSearchParams])
  let allParams = { ...receivePathParams, ...receiveQueryParams }
  const { user } = useAuth()
  const {
    jsonForm,
    searchData,
    setFormData,
    setComponentPropsData,
    tabParamActive,
    getCurrentUi,
    currentSearch,
    isUiChanged,
    setIsUiChanged,
    setTableRefresh,
  } = designContext
  const favorites = jsonForm.favorites

  const isRender = objectPath.get(isUiChanged, `${uischema.key}.isRender`)
  const formData = objectPath.get(jsonForm, 'formData') || {}
  const favoriteTemplateTypeCode = objectPath.get(
    uischema,
    'favorite.favoriteTemplateTypeCode',
  )
  const favoriteKey = objectPath.get(uischema, 'favorite.key')
  const favoriteItem = useMemo(() => {
    if (!isOpen) return {}
    let favoriteItem
    let otherFavoriteItem
    let tabForm = objectPath.get(isUiChanged, 'tabForm')
    if (tabForm) {
      Object.values(tabForm).forEach(({ jsonForm }) => {
        let favorites = jsonForm.favorites
        let found = favorites.find((item) => item.key === favoriteKey)
        if (found) {
          otherFavoriteItem = found
        }
      })
    }
    if (favorites) {
      favoriteItem = favorites.find((item) => item.key === favoriteKey)
    }
    return otherFavoriteItem || favoriteItem || {}
  }, [isOpen])

  const favoriteJsonName = objectPath.get(favoriteItem, `jsonName`)
  const mapFieldFavorite =
    objectPath.get(favoriteItem, 'arraySelect.mapField') || []
  const favoriteSelectType = objectPath.get(favoriteItem, 'selectType')

  let { searchDataFromPage = {} } = config || {}
  let jsonTable = isChart
    ? chartData
    : objectPath.get(jsonForm.formData, uischema.key) ||
      objectPath.get(jsonForm.formData, uischema.name) ||
      []
  const [tab, setTab] = useState('1')
  const [openEdit, setOpenEdit] = useState(false)
  const [favoriteList, setFavoriteList] = useState([])
  const [filterList, setFilterList] = useState([])
  const [columnList, setColumnList] = useState({})
  const [keyword, setKeyword] = useState('')
  const [favorite, setFavorite] = useState({})
  const [tableData, setTableData] = useState(jsonTable)
  const [searchPath, setSearchPath] = useState('')
  const [searchString, setSearchString] = useState('')
  const [loading, setLoading] = useState(false)

  const search = useMemo(() => {
    let search = jsonForm.search || {}
    if (currentSearch?.uiKey && currentSearch?.search) {
      search = currentSearch?.search
    }
    return search
  }, [currentSearch?.uiKey])

  const tableTheme = createTheme({
    overrides: {
      MuiMenu: {
        paper: {
          backgroundColor: 'white',
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: 'transparent',
          maxWidth: '100%',
          '& [class*="Component-horizontalScrollContainer-"]': {
            backgroundColor: 'transparent',
            display: 'inline-grid',
            width: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
          },
          '& [class*="MuiToolbar-regular-"]': {
            minHeight: 0,
          },
        },
      },
      MuiTableFooter: {
        root: {
          '& [class*="MuiTableRow-"]': {
            backgroundColor: 'transparent !important',
          },
          '& [class*="MuiToolbar-"]': {
            display: 'flex',
            backgroundColor: 'transparent !important',
          },
          '& [class*="ForwardRef(MTablePaginationRef)-"]': {
            backgroundColor: 'transparent !important',
          },
          '& [class*="MuiIconButton-"]': {
            backgroundColor: 'transparent !important',
          },
        },
      },
      MuiList: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
  })

  const getFavoriteColumnTitle = (column = {}) => {
    const found = mapFieldFavorite.find(({ field }) => field === column.field)
    if (!found) return column.title
    if (found.title) return found.title
    if (found.label) {
      const label = String(found.label).split('|')[0].trim()
      const labelList = label.split('>')
      return labelList[labelList.length - 1].trim()
    }
    return column.title
  }

  const favoriteColumnHeaderStyle = {
    backgroundColor: '#f5f6f8',
    fontWeight: 'bold',
  }

  const mapFavoriteColumnTitle = (column = {}) => ({
    ...column,
    title: getFavoriteColumnTitle(column),
    headerStyle: {
      ...column.headerStyle,
      ...favoriteColumnHeaderStyle,
    },
    detailColumn: Array.isArray(column.detailColumn)
      ? column.detailColumn.map(mapFavoriteColumnTitle)
      : column.detailColumn,
  })

  const orderFavoriteColumn = (column = []) => {
    if (mapFieldFavorite.length === 0) return column
    const filterColumn = mapFieldFavorite
      .map(({ field }) => column.find((item) => item.field === field))
      .filter((item) => item)
    return filterColumn.length > 0 ? filterColumn : column
  }

  const filterFavorite = (keyword, favoriteList = []) => {
    setKeyword(keyword)
    favoriteList = Array.isArray(favoriteList) ? favoriteList : []
    let keywordText = `${keyword || ''}`.toLowerCase()
    let filterList = favoriteList.filter(({ name }) =>
      `${name || ''}`.toLowerCase().includes(keywordText),
    )

    let columnList = {}

    filterList.map((item, itemIndex) => {
      let { favoriteTemplateId, jsonData = {} } = item
      let columnListKey =
        favoriteTemplateId != null
          ? String(favoriteTemplateId)
          : `favorite-${itemIndex}`
      let jsonDataList = Object.keys(jsonData || {})
      jsonDataList.forEach((key) => {
        let value = objectPath.get(jsonData, key)
        if (Array.isArray(value)) {
          let column = createColumn(value) || []
          let filterColumn = orderFavoriteColumn(column)
          filterColumn = filterColumn.map(mapFavoriteColumnTitle)
          objectPath.set(columnList, [columnListKey, key], filterColumn)
          filterColumn.forEach(({ field, prefixKey }) => {
            value.map((rowValue) => {
              if (!prefixKey) return rowValue
              if (!objectPath.has(rowValue, prefixKey)) {
                let splitKey = prefixKey.split('.')
                let fullKey = []
                splitKey.forEach((key) => {
                  fullKey.push(key)
                  if (!objectPath.has(rowValue, fullKey)) {
                    objectPath.set(rowValue, fullKey, {})
                  }
                })
              } else if (!objectPath.get(rowValue, prefixKey)) {
                objectPath.set(rowValue, prefixKey, {})
              }

              if (!objectPath.get(rowValue, field)) {
                objectPath.set(rowValue, field, undefined)
              } else if (
                objectPath.has(rowValue, field) &&
                !objectPath.get(rowValue, field)
              ) {
                objectPath.set(rowValue, field, undefined)
              }
              return rowValue
            })
          })
          objectPath.set(jsonData, key, value)
        }
      })
      return item
    })
    setFilterList(filterList)
    setColumnList(columnList)
  }

  const onCloseModal = () => {
    setIsOpen(false)
    setFavorite({})
  }

  const onAction = {
    setLoading: setLoading,
    setPage: () => {},
    setRowsPerPage: () => {},
    setTotalCount: () => {},
    setTableColumn: () => {},
    setTableData: setTableData,
    setSearchPath: setSearchPath,
    setSearchString: setSearchString,
    setComponentPropsData: setComponentPropsData,
    setFormData: setFormData,
    updateData: () => {},
  }

  const renderTableData = createRenderTableData({
    jsonForm,
    isUiChanged,
    formData,
    searchData,
    searchDataFromPage,
    allParams,
    tabParamActive,
    search,
    dynamicData,
    onAction,
    searchPath,
    searchString,
    tableData,
    loading,
    designContext,
    setFormData,
    setTableRefresh,
    setLoading,
  })

  const selectFavorite = createSelectFavorite({
    uischema,
    favorite,
    favoriteJsonName,
    favoriteSelectType,
    mapFieldFavorite,
    formData,
    designContext,
    allParams,
    user,
    jsonForm,
    isUiChanged,
    setFormData,
    setIsUiChanged,
    renderTableData,
    onCloseModal,
    getCurrentUi,
  })

  const editFavorite = (favorite) => {
    let data = cloneDeep(favorite)
    objectPath.set(data, 'favoriteTemplateTypeCode', favoriteTemplateTypeCode)
    objectPath.set(isUiChanged, 'editFavorite', data)
    setIsUiChanged(isUiChanged)
    setOpenEdit(true)
  }

  const getFavorite = async (isPublic) => {
    let type = isPublic === '1' ? 'public' : 'me'
    let api = `/core/favorite-template/${favoriteTemplateTypeCode}/${type}`
    let favoriteList = await httpClient
      .get(api)
      .then(({ data }) => data.data)
      .catch(() => [])

    setFavoriteList(favoriteList)
    filterFavorite(keyword, favoriteList)
  }

  const deleteFavorite = (favorite) => {
    let favoriteTemplateId = favorite.favoriteTemplateId

    Swal.fire({
      title: "<h3 class='mb-0 text-danger'>ต้องการลบ Favorite ใช่หรือไม่</h3>",
      icon: 'error',
      iconHtml: '<i class="feather icon-trash text-danger"></i>',
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'ลบ',
      showCancelButton: true,
      cancelButtonText: 'ยกเลิก',
    }).then((result) => {
      if (result.isConfirmed) {
        httpClient
          .delete(`core/favorite-template/${favoriteTemplateId}`)
          .then(({ data }) => {
            Swal.fire({
              title: 'ลบ Favorite สำเร็จ',
              description: JSON.stringify(data.data),
              icon: 'success',
              confirmButtonColor: '#7367f0',
              confirmButtonText: 'ปิด',
            }).then(async (result) => {
              if (result.isConfirmed) {
                getFavorite(tab)
              }
            })
          })
          .catch(() => {
            Swal.fire({
              icon: 'error',
              title: 'ไม่สามารถลบ Favorite ได้',
              text: 'กรุณาลองใหม่อีกครั้ง',
              confirmButtonText: 'ปิด',
            })
          })
      }
    })
  }

  const hasFavoriteValue = (value) =>
    value !== undefined && value !== null && `${value}`.trim() !== ''

  const getFavoriteItemCount = (jsonData = {}) => {
    return Object.keys(jsonData || {}).reduce((total, key) => {
      let value = objectPath.get(jsonData, key)
      if (Array.isArray(value)) return total + value.length
      if (hasFavoriteValue(value)) return total + 1
      return total
    }, 0)
  }

  useEffect(() => {
    if (isOpen) {
      getFavorite(tab)
    }
    return () => {
      setFavoriteList([])
      setFilterList([])
      setColumnList({})
      setKeyword('')
    }
  }, [isOpen])

  useEffect(() => {
    if (isRender) {
      objectPath.set(isUiChanged, `${uischema.key}.isRender`, false)
      setIsUiChanged(isUiChanged)
    }
  }, [isRender])

  if (!isOpen) return ''

  return (
    <>
      <Modal
        show={isOpen}
        fullscreen="true"
        dialogClassName={styles.modalFullScreen}
        className="pl-0"
      >
        <Modal.Header
          className="px-4 py-3"
          closeButton
          onHide={onCloseModal}
          style={{ borderBottom: '1px solid #e8eef5' }}
        >
          <div>
            <h5 className="mb-0">เลือกสูตร</h5>
            <div className="text-muted small mt-1">
              {favoriteTemplateTypeCode}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ backgroundColor: '#f8fafc' }}>
          <MuiThemeProvider theme={themeDefault}>
            <div
              style={{
                margin: '0 auto',
                maxWidth: 1600,
                padding: '16px 20px 24px',
              }}
            >
              <div
                style={{
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #dfe7ef',
                  boxShadow: '0 10px 18px rgba(15, 23, 42, 0.08)',
                  margin: '-16px -20px 14px',
                  padding: '16px 20px 12px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 5,
                }}
              >
                <div
                  className="bg-white mb-3"
                  style={{
                    border: '1px solid #e8eef5',
                    borderRadius: 8,
                    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.04)',
                    padding: 12,
                  }}
                >
                  <Row className="align-items-center">
                    <Col md="8" sm="12" className="mb-2 mb-md-0">
                      <InputGroup>
                        <Form.Control
                          autoComplete="off"
                          placeholder="ค้นหาชื่อสูตร"
                          name="keyword"
                          onChange={(e) =>
                            filterFavorite(e.target.value, favoriteList)
                          }
                          value={keyword}
                          style={{
                            borderColor: '#dfe7ef',
                            minHeight: 38,
                          }}
                        />
                        <InputGroup.Append>
                          <InputGroup.Text className="px-2 bg-white text-secondary">
                            <span className="px-1">
                              <FeatherIcon icon="search" size="14" />
                            </span>
                          </InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    </Col>
                    <Col
                      md="4"
                      sm="12"
                      className="d-flex justify-content-md-end"
                    >
                      <ToggleButtonGroup
                        type="radio"
                        name="favoriteScope"
                        value={tab}
                        onChange={(nextTab) => {
                          if (!nextTab) return
                          setTab(nextTab)
                          getFavorite(nextTab)
                        }}
                        style={{
                          backgroundColor: '#fff',
                          border: '1px solid #dfe7ef',
                          borderRadius: 6,
                          overflow: 'hidden',
                        }}
                      >
                        <ToggleButton
                          className="px-4"
                          value="1"
                          variant="outline-secondary"
                          style={{
                            border: 0,
                            borderRadius: 0,
                            minWidth: 88,
                          }}
                        >
                          Public
                        </ToggleButton>
                        <ToggleButton
                          className="px-4"
                          value="0"
                          variant="outline-secondary"
                          style={{
                            border: 0,
                            borderLeft: '1px solid #dfe7ef',
                            borderRadius: 0,
                            minWidth: 88,
                          }}
                        >
                          Private
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Col>
                  </Row>
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between px-1">
                  <span className="text-muted small">
                    พบ {filterList.length} สูตร
                  </span>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 8 }}
                  >
                    {favorite.favoriteTemplateId ? (
                      <span
                        className="text-primary font-weight-bold"
                        style={{ fontSize: 14 }}
                      >
                        เลือก: {favorite.name || '-'}
                      </span>
                    ) : (
                      <span className="text-muted small">
                        ยังไม่ได้เลือกสูตร
                      </span>
                    )}
                    <Button
                      className="d-inline-flex align-items-center"
                      disabled={!favorite.favoriteTemplateId}
                      onClick={selectFavorite}
                      size="sm"
                      variant="outline-primary"
                      style={{
                        fontSize: 14,
                        height: 30,
                        lineHeight: 1,
                      }}
                    >
                      <FeatherIcon
                        icon="check-circle"
                        size="14"
                        className="mr-1"
                      />
                      เลือก
                    </Button>
                    <Button
                      className="d-inline-flex align-items-center"
                      onClick={onCloseModal}
                      size="sm"
                      variant="outline-secondary"
                      style={{
                        fontSize: 14,
                        height: 30,
                        lineHeight: 1,
                      }}
                    >
                      <FeatherIcon icon="x" size="14" className="mr-1" />
                      ปิด
                    </Button>
                  </div>
                </div>
              </div>

              {filterList.length > 0 ? (
                filterList.map((item, itemIndex) => {
                  let { name, favoriteTemplateId, jsonData = {} } = item
                  let columnListKey =
                    favoriteTemplateId != null
                      ? String(favoriteTemplateId)
                      : `favorite-${itemIndex}`
                  let jsonDataList = Object.keys(jsonData || {})
                  let isSelected =
                    favoriteTemplateId === favorite.favoriteTemplateId
                  let favoriteItemCount = getFavoriteItemCount(jsonData)

                  jsonDataList.sort()
                  return (
                    <div
                      key={
                        favoriteTemplateId != null
                          ? String(favoriteTemplateId)
                          : `favorite-${itemIndex}`
                      }
                      style={{
                        backgroundColor: isSelected ? '#f5fbff' : '#fff',
                        border: `1px solid ${
                          isSelected ? '#21b0f2' : '#e4ebf2'
                        }`,
                        borderLeft: `4px solid ${
                          isSelected ? '#21b0f2' : '#d9e3ec'
                        }`,
                        borderRadius: 8,
                        boxShadow: isSelected
                          ? '0 0 0 2px rgba(33, 176, 242, 0.12)'
                          : '0 1px 4px rgba(15, 23, 42, 0.04)',
                        cursor: 'pointer',
                        overflow: 'hidden',
                      }}
                      className="mb-3"
                      onClick={() => {
                        setFavorite(item)
                      }}
                    >
                      <div
                        className="d-flex align-items-start p-3"
                        style={{
                          borderBottom: '1px solid #edf2f7',
                          gap: 12,
                        }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: isSelected
                              ? '#e6f6ff'
                              : '#f2f5f8',
                            borderRadius: 8,
                            color: isSelected ? '#21b0f2' : '#718096',
                            flex: '0 0 40px',
                            height: 40,
                            width: 40,
                          }}
                        >
                          <FeatherIcon
                            icon={isSelected ? 'check-circle' : 'file-text'}
                            size="18"
                          />
                        </div>
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <div className="text-muted small mb-1">ชื่อสูตร</div>
                          <div
                            className="font-weight-bold"
                            style={{
                              color: '#1f2937',
                              fontSize: 15,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {name || '-'}
                          </div>
                          <div className="text-muted small mt-1">
                            {favoriteItemCount} รายการ
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <Button
                            aria-label="แก้ไขสูตร"
                            className="p-0 mr-2 d-inline-flex align-items-center justify-content-center"
                            title="แก้ไขสูตร"
                            variant="outline-warning"
                            style={{
                              height: 32,
                              lineHeight: 1,
                              width: 32,
                            }}
                            onClick={(event) => {
                              event.stopPropagation()
                              editFavorite(item)
                            }}
                          >
                            <FeatherIcon icon="edit" size="15" />
                          </Button>
                          <Button
                            aria-label="ลบสูตร"
                            className="p-0 d-inline-flex align-items-center justify-content-center"
                            title="ลบสูตร"
                            variant="outline-danger"
                            style={{
                              height: 32,
                              lineHeight: 1,
                              width: 32,
                            }}
                            onClick={(event) => {
                              event.stopPropagation()
                              deleteFavorite(item)
                            }}
                          >
                            <FeatherIcon icon="trash" size="15" />
                          </Button>
                        </div>
                      </div>

                      <div className="px-3 pb-3 pt-2">
                        <div className="text-muted small font-weight-bold mb-2">
                          รายการสูตร
                        </div>
                        {jsonDataList.length > 0 ? (
                          <Row className="mx-n1">
                            {jsonDataList.map((key) => {
                              let value = objectPath.get(jsonData, key)
                              if (Array.isArray(value)) {
                                let column =
                                  objectPath.get(columnList, [
                                    columnListKey,
                                    key,
                                  ]) || []
                                return (
                                  <Col
                                    key={key}
                                    sm="12"
                                    className="px-1 mb-2"
                                  >
                                    {jsonDataList.length > 1 ? (
                                      <div className="text-muted small mb-1">
                                        {key}
                                      </div>
                                    ) : null}
                                    <div
                                      style={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #edf2f7',
                                        borderRadius: 6,
                                        maxHeight: 240,
                                        overflow: 'auto',
                                      }}
                                    >
                                      <MuiThemeProvider theme={tableTheme}>
                                        <Table
                                          title={name}
                                          data={value}
                                          columns={column}
                                          options={{
                                            draggable: false,
                                            emptyRowsWhenPaging: false,
                                            paging: false,
                                            padding: 'dense',
                                            search: false,
                                            showTitle: false,
                                            sorting: false,
                                            toolbar: false,
                                          }}
                                        />
                                      </MuiThemeProvider>
                                    </div>
                                  </Col>
                                )
                              } else {
                                return (
                                  <Col
                                    key={key}
                                    sm="12"
                                    md="6"
                                    className="px-1 mb-2"
                                  >
                                    <div
                                      style={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #edf2f7',
                                        borderRadius: 6,
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <div
                                        className="font-weight-bold px-2 py-1"
                                        style={{ backgroundColor: '#f8fafc' }}
                                      >
                                        {key}
                                      </div>
                                      <div
                                        className="px-2 py-2"
                                        style={{
                                          overflowWrap: 'anywhere',
                                          whiteSpace: 'pre-wrap',
                                        }}
                                      >
                                        {hasFavoriteValue(value)
                                          ? `${value}`
                                          : '-'}
                                      </div>
                                    </div>
                                  </Col>
                                )
                              }
                            })}
                          </Row>
                        ) : (
                          <div className="text-center text-muted py-4">
                            ไม่พบรายการสูตร
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <div
                  className="bg-white text-center text-muted py-5"
                  style={{
                    border: '1px dashed #d9e3ec',
                    borderRadius: 8,
                  }}
                >
                  ไม่พบสูตร
                </div>
              )}
            </div>
          </MuiThemeProvider>
        </Modal.Body>
      </Modal>
      <ModalCreate
        uischema={uischema}
        menu={menu}
        isOpen={openEdit}
        setIsOpen={setOpenEdit}
        isUpdated={true}
        onFetch={() => getFavorite(tab)}
        isTable={uischema.tagname === 'table'}
        config={config}
        dynamicData={dynamicData}
      />
    </>
  )
}

export default ModalSelect
